import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Plus, Trash2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Budgets = () => {
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [currentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [formData, setFormData] = useState({
    amount: '',
    month: currentMonth,
    categoryId: '',
  });

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  const fetchData = async () => {
    try {
      const [budgetRes, catRes, transRes] = await Promise.all([
        axios.get(`${API}/budgets?month=${currentMonth}`, config),
        axios.get(`${API}/categories`, config),
        axios.get(`${API}/transactions`, config),
      ]);

      setBudgets(budgetRes.data);
      setCategories(catRes.data.filter(cat => cat.type === 'expense'));
      const transactionsData = transRes.data.data || [];
      setTransactions(transactionsData.filter(t => t.type === 'expense' && t.date.startsWith(currentMonth)));
    } catch (error) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/budgets`, formData, config);
      toast.success('Budget created');
      setShowDialog(false);
      setFormData({ amount: '', month: currentMonth, categoryId: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create budget');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this budget?')) return;

    try {
      await axios.delete(`${API}/budgets/${id}`, config);
      toast.success('Budget deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete budget');
    }
  };

  const calculateSpent = (categoryId) => {
    return transactions
      .filter(t => t.categoryId === categoryId)
      .reduce((sum, t) => sum + t.amount, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const budgetsWithProgress = budgets.map(budget => {
    const spent = calculateSpent(budget.categoryId);
    const percentage = (spent / budget.amount) * 100;
    const exceeded = spent > budget.amount;
    return { ...budget, spent, percentage, exceeded };
  });

  const exceededBudgets = budgetsWithProgress.filter(b => b.exceeded);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="budgets-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" data-testid="budgets-title">Budgets</h1>
          <p className="text-muted-foreground mt-2">Set and track your spending limits</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="add-budget-button">
          <Plus className="mr-2 h-4 w-4" />
          Add Budget
        </Button>
      </div>

      {/* Budget Alerts */}
      {exceededBudgets.length > 0 && (
        <Alert variant="destructive" data-testid="budget-exceeded-alert">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Budget Exceeded!</AlertTitle>
          <AlertDescription>
            You have exceeded the budget for {exceededBudgets.length} {exceededBudgets.length === 1 ? 'category' : 'categories'}:
            {exceededBudgets.map(b => ` ${b.categoryName}`).join(',')}
          </AlertDescription>
        </Alert>
      )}

      {/* Budget List */}
      <Card data-testid="budgets-table-card">
        <CardHeader>
          <CardTitle>Current Month Budgets ({currentMonth})</CardTitle>
        </CardHeader>
        <CardContent>
          {budgetsWithProgress.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              <p>No budgets set for this month</p>
              <p className="text-sm mt-2">Click "Add Budget" to create one</p>
            </div>
          ) : (
            <div className="space-y-6">
              {budgetsWithProgress.map((budget) => (
                <div key={budget.id} className="space-y-2" data-testid={`budget-item-${budget.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{budget.categoryName}</h3>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(budget.spent)} of {formatCurrency(budget.amount)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(budget.id)}
                      data-testid={`delete-budget-${budget.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <Progress
                    value={Math.min(budget.percentage, 100)}
                    className={budget.exceeded ? 'bg-red-100' : ''}
                    data-testid={`budget-progress-${budget.id}`}
                  />
                  <p className={`text-xs ${budget.exceeded ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                    {budget.exceeded ? `Exceeded by ${formatCurrency(budget.spent - budget.amount)}` : `${formatCurrency(budget.amount - budget.spent)} remaining`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle>Add Budget</DialogTitle>
            <DialogDescription>Set a spending limit for a category</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.categoryId} onValueChange={(value) => setFormData({ ...formData, categoryId: value })}>
                <SelectTrigger data-testid="budget-category-select">
                  <SelectValue placeholder="Select expense category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Budget Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="10000"
                required
                data-testid="budget-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Month</Label>
              <Input
                type="month"
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                required
                data-testid="budget-month-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-budget-button">
                Cancel
              </Button>
              <Button type="submit" data-testid="save-budget-button">
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Budgets;